import React, { useCallback, useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";

import Loader from "@components/shared/Loader/Loader";
import UnitDetailsBlock from "@components/Unit/UnitDetailsBlock/UnitDetailsBlock";
import { CONTACT_PERSON_TYPES } from "@constants/contactPerson";
import {
  ResourceInfoType,
  UnitDetailsResponseType,
} from "@models/components/units.model";
import { getLocationType, locationType } from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import { ContactPersonInfoType } from "@models/shared/shared.model";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import { RootState } from "@store/index";

const ResourceDetailsPageComponent: React.FC = () => {
  const [resourceDetails, setResourceDetails] = useState<
    Array<ResourceInfoType>
  >([] as Array<ResourceInfoType>);
  const [resourceInfo, setResourceInfo] = useState<ResourceInfoType>(
    {} as ResourceInfoType
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [contactPersonDetails, setContactPersonDetails] = useState(
    [] as Array<ContactPersonInfoType>
  );

  const locations: [getLocationType, locationType, userProfile] =
    useOutletContext();

  const locationId = locations?.[1]?.id || "";

  const { resourceId } = useSelector((State: RootState) => State.organisation);

  const { t } = useTranslation();

  useEffect(() => {
    if (locationId) getUnitDetails(locationId);
  }, [locationId]);

  useEffect(() => {
    getContactPerson(resourceInfo?.id);
  }, [resourceInfo?.id]);

  const getContactPerson = (resourceId: string) => {
    if (resourceId) getContactPersonCallback(resourceId);
    else setContactPersonDetails([] as Array<ContactPersonInfoType>);
  };

  const getContactPersonCallback = useCallback(async (locationId: string) => {
    const data = await API_SERVICES.CONTACT_PERSON.GET_CONTACT_PERSON(
      locationId,
      CONTACT_PERSON_TYPES.RESOURCE
    );
    const responseData = handleResponse(data);

    if (responseData?.ok) {
      setContactPersonDetails(responseData.data);
    } else setContactPersonDetails([] as Array<ContactPersonInfoType>);
  }, []);

  const getUnitDetails = useCallback(async (locationId: string) => {
    setIsFetching(true);
    const data = await API_SERVICES.UNIT.DETAILS(locationId);
    const responseData = handleResponse(data);

    if (responseData?.ok) {
      setResourceDetails(responseData.data);
      setInitialUnit(responseData.data);
      setIsFetching(false);
    } else setIsFetching(false);
  }, []);

  const handleTabChange = (tab: ResourceInfoType) => {
    setResourceInfo(tab);
  };

  const setInitialUnit = (unitsDetails: UnitDetailsResponseType) => {
    if (resourceId !== "0") {
      unitsDetails?.resources?.filter(obj => {
        if (obj.id === resourceId) {
          setResourceInfo(obj);
        }
      });
    } else {
      setResourceInfo({} as ResourceInfoType);
    }
  };

  return (
    <div className="fg-unit-section-wrapper">
      <div className="container">
        <div className="mt-3 ">
          <h4 className="mb-1">
            {locations?.[1]?.resourceType &&
              applyLocalization(
                (resourceDetails?.length ?? 0) > 1
                  ? locations?.[1]?.resourceType.terminology.plural
                  : locations?.[1]?.resourceType.terminology.singular
              )}
          </h4>
        </div>
        <div
          className="tab-content"
          id="pills-tabContent"
          data-testid="resourceDetails-test"
        >
          <div
            className="tab-pane fade show active"
            id="pills-units"
            role="tabpanel"
            aria-labelledby="info-tab"
          >
            <div
              className="fg-tab-content-wrap"
              data-testid="resourceDetailsPage"
            >
              {isFetching && <Loader isLoading={true} />}
              {!isFetching && resourceDetails?.length > 0 && (
                <UnitDetailsBlock
                  resourceInfo={resourceInfo}
                  resourceDetails={resourceDetails}
                  handleTabChange={handleTabChange}
                  contactList={contactPersonDetails}
                  locationId={locationId}
                />
              )}
              {!isFetching && resourceDetails?.length <= 0 && (
                <div className="container fg-tab-welcome-block fg-border-none">
                  {t("noUnitsAvailable", {
                    Units: applyLocalization(
                      locations[1]?.resourceType?.terminology.plural
                    ),
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResourceDetailsPage = withTranslation()(ResourceDetailsPageComponent);

export default ResourceDetailsPage;
